import { TEXT_ICON_BUTTON_TYPES } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { useCallback } from 'react';
import amplitude from 'src/_shared/amplitude';
import { FeatureFlagAmplitude } from 'src/_shared/featureFlag';
import { logError } from 'src/_shared/logger';
import { isNewEditor } from 'src/_shared/utils/is-new-editor/isNewEditor';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { useStore } from 'src/contexts/StoreContext';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { IProductModel } from 'src/store/_models/product';
import { PRODUCT_STATUS } from 'src/store/_models/product.constants';
import { DYNAMIC_MODAL_GROUPS, TOAST_TYPES } from 'src/store/toast/toastStore.types';

export function useProductLogic() {
  const {
    i18n,
    dynamicModal,
    project: { switchToGroup, activeProject },
    toastStore: { showToast },
  } = useStore();

  const deleteProducts = useCallback(
    ({ deletedProducts = [] }: { deletedProducts?: IProductModel[] }) => {
      const productsDeletedCount = deletedProducts.length;

      if (productsDeletedCount && productsDeletedCount > 0) {
        deletedProducts.forEach(deletedProduct => deletedProduct.setStatus(PRODUCT_STATUS.REMOVING));
        const isMVP4 = amplitude.getVariantValue(FeatureFlagAmplitude.EDITOR_UNICO_HEADER_V4);
        const defaultToastParams = {
          id: `delete-product-toast-${deletedProducts.map(p => p.id).join('-')}`,
          duration: 3000,
          group: DYNAMIC_MODAL_GROUPS.Delete_Product,
        };
        if (isMVP4 && isNewEditor()) {
          showToast({
            ...defaultToastParams,
            onAfterClose: () => {
              deletedProducts
                .filter(deletedProduct => deletedProduct.status === PRODUCT_STATUS.REMOVING)
                .forEach(deletedProduct => deletedProduct.remove());
            },
            onPress: () => {
              // undo delete products
              deletedProducts.forEach(deletedProduct => deletedProduct.setStatus(PRODUCT_STATUS.IDLE));
            },
            removeOnPress: true,
            type: TOAST_TYPES.Success,
            ctaIconType: TEXT_ICON_BUTTON_TYPES.ICON_RIGHT,
            ctaIconName: ICON_NAMES.Undo,
            ctaText: i18n.t('customize:undo'),
            message: i18n.t('toast:products-removed', { count: productsDeletedCount }),
          });
        } else {
          Promise.all(
            deletedProducts
              .filter(deletedProduct => deletedProduct.status === PRODUCT_STATUS.REMOVING)
              .map(deletedProduct => deletedProduct.remove()),
          ).then(() => {
            showToast({
              ...defaultToastParams,
              message:
                productsDeletedCount > 1
                  ? i18n.t('toast:products-deleted-successfully')
                  : i18n.t('toast:product-deleted-successfully'),
            });
          });
        }
      }

      return undefined;
    },
    [i18n, showToast],
  );

  const removeProduct = ({ product, sectionName }: { product: IProductModel; sectionName?: SECTION_NAMES }) => {
    dynamicModal.openModal({
      dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteAll,
      onPressDeleteProduct: () => {
        try {
          // if is last element of the group and there are other available groups, switch to the first available
          const isLastElement = activeProject?.groups.find(g => g.id === product.groupId)?.products.length === 1;
          const otherGroups = activeProject?.filteredGroups.filter(g => g.id !== product.groupId);
          if (isLastElement && otherGroups && otherGroups.length > 0) {
            const firstFilledGroup = otherGroups[0];
            switchToGroup({ groupId: firstFilledGroup.id, scrollTo: 'top' });
          }
        } catch (error) {
          logError(error);
        }
      },
      productToDelete: product,
      originSectionName: sectionName,
    });
  };

  return { deleteProducts, removeProduct };
}
